import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { useSearchParams } from 'react-router-dom';
import axios from 'axios';
import {
  BookOpen, Clock, Calendar, MapPin, GraduationCap,
  Heart, Loader2, CheckCircle, Users, Sparkles,
  CreditCard, Lock, AlertCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import SectionHeading from '../components/SectionHeading';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const weeklyRotation = [
  { week: 1, title: "Qur'an & Tajweed", color: 'primary',
    topics: ['Makharij (pronunciation points)', 'Tajweed rules', 'Reading correction'] },
  { week: 2, title: 'Aqeedah & Islamic Foundations', color: 'secondary',
    topics: ['Tawheed', 'Pillars of Iman', 'Understanding worship'] },
  { week: 3, title: 'Fiqh of Everyday Life', color: 'primary',
    topics: ['Salah', 'Purification', 'Fasting', 'Family and social responsibilities'] },
  { week: 4, title: 'Seerah / Tafsir / Hadith', color: 'secondary',
    topics: ["Life of the Prophet (peace be upon him)", "Lessons from Qur'anic verses", 'Character development'] },
];

const pricingTiers = [
  { label: '1 Adult',  amount: 65 },
  { label: '2 Adults', amount: 105 },
  { label: '3 Adults', amount: 130 },
];

const AdultEducationPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [submitted, setSubmitted] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isCheckingPayment, setIsCheckingPayment] = useState(false);
  const [isRedirecting, setIsRedirecting] = useState(false);
  const [paymentCompleted, setPaymentCompleted] = useState(false);
  const [paymentSessionId, setPaymentSessionId] = useState('');
  const [selectedTier, setSelectedTier] = useState(null);
  const [isRecurring, setIsRecurring] = useState(true);
  const [formData, setFormData] = useState({
    firstName: '', lastName: '', cellPhone: '', email: '',
    homeAddress: '', emergencyFullName: '', emergencyRelationship: '',
    emergencyCell: '', additionalNotes: '',
  });

  // Restore state from localStorage after Stripe redirect
  useEffect(() => {
    const saved = localStorage.getItem('tnmc_adult_form');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        if (parsed.formData) setFormData(parsed.formData);
        if (parsed.selectedTier) setSelectedTier(parsed.selectedTier);
        if (typeof parsed.isRecurring === 'boolean') setIsRecurring(parsed.isRecurring);
      } catch { /* ignore */ }
    }
  }, []);

  // Verify payment when returning from Stripe
  useEffect(() => {
    const status = searchParams.get('payment');
    const sid = searchParams.get('session_id');
    if (status === 'success' && sid) {
      setIsCheckingPayment(true);
      axios.get(`${API}/adult-education/payment-status/${sid}`)
        .then((res) => {
          if (res.data.paid) {
            setPaymentCompleted(true);
            setPaymentSessionId(sid);
            toast.success('Payment successful — please complete the form below and submit.');
            // scroll to form
            setTimeout(() => document.getElementById('adult-registration-form')?.scrollIntoView({ behavior: 'smooth' }), 400);
          } else {
            toast.error('Payment could not be verified. Please try again.');
          }
        })
        .catch(() => toast.error('Payment verification failed. Please try again.'))
        .finally(() => setIsCheckingPayment(false));
      // clean the URL params so refresh doesn't re-verify
      searchParams.delete('payment');
      searchParams.delete('session_id');
      setSearchParams(searchParams, { replace: true });
    } else if (status === 'cancelled') {
      toast.info('Payment was cancelled. You can try again when ready.');
      searchParams.delete('payment');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams]);

  const update = (field, value) => setFormData((p) => ({ ...p, [field]: value }));

  const handlePayment = async () => {
    if (!selectedTier) { toast.error('Please select a pricing tier first'); return; }
    // Save current form state so it survives the Stripe redirect
    localStorage.setItem('tnmc_adult_form', JSON.stringify({ formData, selectedTier, isRecurring }));
    setIsRedirecting(true);
    try {
      const res = await axios.post(`${API}/adult-education/checkout`, {
        amount: selectedTier.amount,
        is_recurring: isRecurring,
        adults_label: selectedTier.label,
        origin_url: window.location.origin,
      });
      if (res.data.url) window.location.href = res.data.url;
    } catch (e) {
      toast.error('Could not start payment. Please try again.');
      setIsRedirecting(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!paymentCompleted || !paymentSessionId) {
      toast.error('Please complete payment before submitting the form.');
      return;
    }
    setIsSubmitting(true);
    try {
      await axios.post(`${API}/adult-education/register`, {
        ...formData,
        paymentSessionId,
        adultsLabel: selectedTier?.label || '',
        paymentAmount: selectedTier?.amount || 0,
        isRecurring,
      });
      localStorage.removeItem('tnmc_adult_form');
      setSubmitted(true);
      window.scrollTo({ top: 0, behavior: 'smooth' });
    } catch (error) {
      const msg = error?.response?.data?.detail || 'Submission failed — please try again.';
      toast.error(typeof msg === 'string' ? msg : 'Submission failed');
    } finally {
      setIsSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-screen pt-24 flex items-center justify-center px-4">
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} className="max-w-2xl text-center" data-testid="adult-success-screen">
          <div className="w-24 h-24 rounded-full bg-green-500/20 flex items-center justify-center mx-auto mb-6">
            <CheckCircle className="h-14 w-14 text-green-500" />
          </div>
          <h1 className="text-4xl font-bold font-['Outfit'] text-foreground mb-6">We have received your form!</h1>
          <div className="p-6 rounded-2xl bg-card border border-border text-left space-y-4 mb-8">
            <p className="text-foreground leading-relaxed">
              <span className="text-primary font-semibold">📧 Further details</span> — We will communicate further details via your provided email ({formData.email}).
            </p>
            <p className="text-foreground leading-relaxed">
              <span className="text-secondary font-semibold">💬 WhatsApp Group</span> — You will be added to our Adult Islamic School WhatsApp group via the phone number you provided ({formData.cellPhone}).
            </p>
            <p className="text-sm text-muted-foreground pt-2 border-t border-border">
              Jazakum Allahu Khairan for signing up. Classes begin <span className="text-secondary font-medium">Saturday, August 22nd at 5:30 PM</span>.
            </p>
          </div>
          <Button onClick={() => window.location.href = '/'} className="rounded-full" data-testid="return-home-btn">Return Home</Button>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24" data-testid="adult-education-page">
      {/* Registration Open Banner */}
      <div className="bg-primary/10 text-foreground py-3 px-4 border-b border-primary/30">
        <div className="max-w-7xl mx-auto flex items-center justify-center gap-3">
          <Sparkles className="h-5 w-5 text-primary" />
          <p className="text-center font-semibold text-base sm:text-lg">
            Adult Classes Registration is <span className="text-primary">OPEN</span> — Classes Start <span className="text-primary">Saturday, August 22nd</span>
          </p>
        </div>
      </div>

      {/* Hero */}
      <section className="py-16 lg:py-24 hero-gradient islamic-pattern">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-3xl">
            <span className="inline-block px-4 py-1 rounded-full bg-primary/10 text-primary text-sm font-medium mb-4">Adults Only • Weekend Islamic School</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground font-['Outfit'] leading-tight mb-6">
              Adult Islamic <span className="text-gradient">Education Program</span>
            </h1>
            <p className="text-lg text-muted-foreground leading-relaxed">
              Grow your understanding of the deen with our weekly adult class. A structured 4-week rotation covering Qur&apos;an, Aqeedah, Fiqh, and Seerah — designed for busy Muslims seeking authentic Islamic knowledge.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Program Details */}
      <section className="py-16 lg:py-24 bg-card border-y border-border">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading badge="Program Details" title="Once a Week Learning" description="A single 90-minute session every Saturday evening" />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
            <div className="p-6 space-y-4 bg-background rounded-2xl border border-border">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-primary/10"><Calendar className="h-6 w-6 text-primary" /></div>
                <h3 className="text-xl font-semibold font-['Outfit'] text-foreground">Schedule</h3>
              </div>
              <div className="space-y-2 text-muted-foreground text-sm">
                <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-primary" /> Saturdays, once a week</p>
                <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-primary" /> 5:30 PM – 7:00 PM</p>
                <p className="flex items-center gap-2 text-secondary font-medium">
                  <Calendar className="h-4 w-4 text-secondary" /> Classes Start: Saturday, August 22nd
                </p>
              </div>
            </div>

            <div className="p-6 space-y-4 bg-background rounded-2xl border border-border">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-secondary/10"><MapPin className="h-6 w-6 text-secondary" /></div>
                <h3 className="text-xl font-semibold font-['Outfit'] text-foreground">Location</h3>
              </div>
              <div className="space-y-2 text-muted-foreground text-sm">
                <p>54 St. Paul Crescent</p>
                <p>Barrie, ON L9J 0M2</p>
              </div>
            </div>

            <div className="p-6 space-y-4 bg-background rounded-2xl border border-border">
              <div className="flex items-center gap-3">
                <div className="p-3 rounded-xl bg-primary/10"><Users className="h-6 w-6 text-primary" /></div>
                <h3 className="text-xl font-semibold font-['Outfit'] text-foreground">Who Can Join?</h3>
              </div>
              <div className="space-y-2 text-muted-foreground text-sm">
                <p>Open to all adults (18+)</p>
                <p>Beginners and continuing learners welcome</p>
                <p>Brothers and sisters</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Weekly Rotation */}
      <section className="py-16 lg:py-24" data-testid="weekly-rotation">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading badge="Curriculum" title="Monthly Learning Rotation" description="Four themed weeks that cycle every month — comprehensive coverage of essential Islamic sciences" />

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {weeklyRotation.map((w, i) => (
              <motion.div
                key={w.week}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className={`p-6 rounded-2xl border ${w.color === 'primary' ? 'bg-primary/5 border-primary/30' : 'bg-secondary/5 border-secondary/30'}`}
                data-testid={`week-${w.week}`}
              >
                <div className="flex items-center gap-2 mb-3">
                  <span className={`px-2 py-1 rounded-full text-xs font-bold ${w.color === 'primary' ? 'bg-primary/20 text-primary' : 'bg-secondary/20 text-secondary'}`}>WEEK {w.week}</span>
                </div>
                <h3 className="text-lg font-semibold text-foreground font-['Outfit'] mb-3">{w.title}</h3>
                <ul className="space-y-1.5">
                  {w.topics.map((t) => (
                    <li key={t} className="text-sm text-muted-foreground flex items-start gap-2">
                      <BookOpen className={`h-3.5 w-3.5 mt-0.5 flex-shrink-0 ${w.color === 'primary' ? 'text-primary' : 'text-secondary'}`} />
                      <span>{t}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Step 1: Select Plan + Pay */}
      <section className="py-16 lg:py-24 bg-card border-y border-border" data-testid="adult-pricing">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            badge="Step 1 of 2"
            title="Select Plan & Complete Payment"
            description="Complete your first month's payment to unlock the registration form"
          />

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
            {pricingTiers.map((tier) => {
              const isSelected = selectedTier?.amount === tier.amount;
              return (
                <button
                  key={tier.label}
                  onClick={() => !paymentCompleted && setSelectedTier(tier)}
                  disabled={paymentCompleted}
                  className={`p-8 rounded-2xl border-2 text-center transition-all ${
                    isSelected ? 'border-primary bg-primary/10' : 'border-border hover:border-primary/50'
                  } ${paymentCompleted ? 'opacity-60 cursor-not-allowed' : 'cursor-pointer'}`}
                  data-testid={`pricing-${tier.amount}`}
                >
                  <p className="text-sm text-muted-foreground mb-2">{tier.label}</p>
                  <p className={`text-4xl font-bold ${isSelected ? 'text-primary' : 'text-foreground'}`}>${tier.amount}</p>
                  <p className="text-xs text-muted-foreground">per month</p>
                </button>
              );
            })}
          </div>

          {/* Recurring toggle */}
          {!paymentCompleted && (
            <div className="p-4 rounded-xl bg-background border border-border mb-6">
              <label className="flex items-center justify-between cursor-pointer">
                <div>
                  <p className="text-sm font-medium text-foreground">Recurring monthly payment</p>
                  <p className="text-xs text-muted-foreground">Your card will be charged monthly — cancel anytime</p>
                </div>
                <div className="relative">
                  <input type="checkbox" checked={isRecurring} onChange={(e) => setIsRecurring(e.target.checked)} className="sr-only" data-testid="adult-recurring-toggle" />
                  <div className={`w-14 h-8 rounded-full transition-colors ${isRecurring ? 'bg-primary' : 'bg-muted'}`}>
                    <div className={`w-6 h-6 rounded-full bg-white shadow-md transform transition-transform absolute top-1 ${isRecurring ? 'translate-x-7' : 'translate-x-1'}`} />
                  </div>
                </div>
              </label>
            </div>
          )}

          {/* Payment status */}
          {paymentCompleted ? (
            <div className="p-6 rounded-2xl bg-green-500/10 border border-green-500/30 flex items-center gap-4" data-testid="payment-success-badge">
              <CheckCircle className="h-8 w-8 text-green-500 flex-shrink-0" />
              <div className="flex-1">
                <p className="font-semibold text-foreground">Payment received</p>
                <p className="text-sm text-muted-foreground">Please complete the registration form below and submit.</p>
              </div>
            </div>
          ) : isCheckingPayment ? (
            <div className="p-6 rounded-2xl bg-primary/10 border border-primary/30 flex items-center gap-4">
              <Loader2 className="h-6 w-6 text-primary animate-spin" />
              <p className="text-foreground">Verifying payment…</p>
            </div>
          ) : (
            <Button
              onClick={handlePayment}
              disabled={!selectedTier || isRedirecting}
              className="w-full h-14 rounded-xl text-lg font-semibold bg-primary text-primary-foreground hover:bg-primary/90 glow-teal disabled:opacity-50"
              data-testid="adult-pay-btn"
            >
              {isRedirecting ? (
                <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Redirecting to Stripe…</>
              ) : (
                <><CreditCard className="mr-2 h-5 w-5" /> {selectedTier ? `Pay $${selectedTier.amount} — Continue to Payment` : 'Select a plan above to continue'}</>
              )}
            </Button>
          )}
        </div>
      </section>

      {/* Step 2: Registration Form */}
      <section className="py-16 lg:py-24" data-testid="adult-registration" id="adult-registration-form">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <SectionHeading
            badge="Step 2 of 2"
            title="Complete Your Registration"
            description={paymentCompleted ? "Fill in your details to finalize your enrollment" : "Locked — complete payment above to unlock this form"}
          />

          {!paymentCompleted && (
            <div className="mb-6 p-4 rounded-xl bg-amber-500/10 border border-amber-500/30 flex items-start gap-3" data-testid="form-locked-notice">
              <Lock className="h-5 w-5 text-amber-500 flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-sm font-medium text-foreground">Form Locked</p>
                <p className="text-xs text-muted-foreground">Please complete payment above before filling out the form. This ensures your spot is reserved.</p>
              </div>
            </div>
          )}

          <fieldset disabled={!paymentCompleted} className={paymentCompleted ? '' : 'opacity-50 pointer-events-none'}>
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Adult Information */}
              <div className="p-6 rounded-2xl bg-card border border-border">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <GraduationCap className="h-5 w-5 text-primary" /> Adult Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">First Name *</label>
                    <Input value={formData.firstName} onChange={(e) => update('firstName', e.target.value)} required={paymentCompleted} data-testid="adult-first-name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Last Name *</label>
                    <Input value={formData.lastName} onChange={(e) => update('lastName', e.target.value)} required={paymentCompleted} data-testid="adult-last-name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Cell Phone *</label>
                    <Input type="tel" value={formData.cellPhone} onChange={(e) => update('cellPhone', e.target.value)} required={paymentCompleted} data-testid="adult-cell" placeholder="For WhatsApp group" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Email Address *</label>
                    <Input type="email" value={formData.email} onChange={(e) => update('email', e.target.value)} required={paymentCompleted} data-testid="adult-email" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-foreground mb-1.5">Home Address *</label>
                    <Input value={formData.homeAddress} onChange={(e) => update('homeAddress', e.target.value)} required={paymentCompleted} data-testid="adult-address" />
                  </div>
                </div>
              </div>

              {/* Emergency Contact */}
              <div className="p-6 rounded-2xl bg-card border border-border">
                <h3 className="text-lg font-semibold text-foreground mb-4 flex items-center gap-2">
                  <Heart className="h-5 w-5 text-secondary" /> Emergency Contact
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className="block text-sm font-medium text-foreground mb-1.5">Full Name *</label>
                    <Input value={formData.emergencyFullName} onChange={(e) => update('emergencyFullName', e.target.value)} required={paymentCompleted} data-testid="emergency-name" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Relationship *</label>
                    <Input value={formData.emergencyRelationship} onChange={(e) => update('emergencyRelationship', e.target.value)} required={paymentCompleted} data-testid="emergency-relationship" placeholder="e.g. Spouse, Parent" />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-foreground mb-1.5">Cell Phone *</label>
                    <Input type="tel" value={formData.emergencyCell} onChange={(e) => update('emergencyCell', e.target.value)} required={paymentCompleted} data-testid="emergency-cell" />
                  </div>
                </div>
              </div>

              {/* Additional Notes */}
              <div className="p-6 rounded-2xl bg-card border border-border">
                <h3 className="text-lg font-semibold text-foreground mb-4">Additional Notes</h3>
                <p className="text-sm text-muted-foreground mb-3">Any allergies, medical conditions, or other information we should know.</p>
                <textarea
                  value={formData.additionalNotes}
                  onChange={(e) => update('additionalNotes', e.target.value)}
                  rows={4}
                  className="w-full px-4 py-3 rounded-xl bg-background border border-border focus:border-primary focus:ring-1 focus:ring-primary text-foreground resize-none"
                  data-testid="additional-notes"
                />
              </div>

              <Button
                type="submit"
                disabled={isSubmitting || !paymentCompleted}
                className="w-full h-14 rounded-xl text-lg font-semibold bg-primary text-primary-foreground hover:bg-primary/90 glow-teal disabled:opacity-50"
                data-testid="adult-submit-btn"
              >
                {isSubmitting ? (
                  <><Loader2 className="mr-2 h-5 w-5 animate-spin" /> Submitting…</>
                ) : !paymentCompleted ? (
                  <><Lock className="mr-2 h-5 w-5" /> Complete Payment First</>
                ) : (
                  <>Submit Registration</>
                )}
              </Button>

              {!paymentCompleted && (
                <p className="text-center text-xs text-muted-foreground flex items-center justify-center gap-1">
                  <AlertCircle className="h-3 w-3" />
                  The submit button unlocks once your payment is confirmed.
                </p>
              )}
            </form>
          </fieldset>
        </div>
      </section>
    </div>
  );
};

export default AdultEducationPage;
